import { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import moment from "moment"
import axios from "axios"
import { baseUrl } from "../../config/baseUrl"
import loaderContext from "../../context/LoaderContext"
import Pagination from "../Pagination/Pagination"

const HISTORY_PAGE_LIMIT = 10

export default function PurchaseHistory() {
    const { setLoader } = useContext(loaderContext)

    const [pageNumber, setPageNumber] = useState(1)
    const [history, setHistory] = useState([])
    const [showFilters, setShowFilters] = useState(false)
    const [searchInput, setSearchInput] = useState("")
    const [filters, setFilters] = useState({
        search: "",
        startDate: "",
        endDate: "",
        status: "",
    })
    const [paginationDetails, setPaginationDetails] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: HISTORY_PAGE_LIMIT,
    })

    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmedSearch = searchInput.trim()

            setFilters((prev) => {
                if (prev.search === trimmedSearch) {
                    return prev
                }

                return {
                    ...prev,
                    search: trimmedSearch,
                }
            })

            setPageNumber(1)
        }, 400)

        return () => clearTimeout(timer)
    }, [searchInput])

    useEffect(() => {
        const fetchPurchaseHistory = async () => {
            setLoader(true)

            try {
                const params = {
                    page: pageNumber,
                    limit: HISTORY_PAGE_LIMIT,
                }

                if (filters.search) {
                    params.search = filters.search
                }

                if (filters.status) {
                    params.status = filters.status
                }

                if (filters.startDate) {
                    params.startDate = `${filters.startDate}T00:00:00.000`
                }

                if (filters.endDate) {
                    params.endDate = `${filters.endDate}T23:59:59.999`
                }

                const response = await axios.get(`${baseUrl}/api/history`, {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                    },
                    params,
                })

                const validData = Array.isArray(response.data?.data)
                    ? response.data.data.filter(Boolean)
                    : []

                setHistory(validData)
                setPaginationDetails({
                    currentPage: response.data?.currentPage || pageNumber,
                    totalPages: response.data?.totalPages || 1,
                    totalRecords: response.data?.totalRecords || 0,
                    limit: HISTORY_PAGE_LIMIT,
                })
            } catch (error) {
                console.log("error", error)
                setHistory([])
                setPaginationDetails({
                    currentPage: 1,
                    totalPages: 1,
                    totalRecords: 0,
                    limit: HISTORY_PAGE_LIMIT,
                })
            } finally {
                setLoader(false)
            }
        }

        fetchPurchaseHistory()
    }, [pageNumber, filters.search, filters.startDate, filters.endDate, filters.status, setLoader])

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }))
        setPageNumber(1)
    }

    const clearFilters = () => {
        setSearchInput("")
        setFilters({
            search: "",
            startDate: "",
            endDate: "",
            status: "",
        })
        setPageNumber(1)
    }

    const hasActiveFilters = () => {
        return Boolean(searchInput.trim() || filters.startDate || filters.endDate || filters.status)
    }

    const activeFilterCount = [
        searchInput.trim(),
        filters.startDate,
        filters.endDate,
        filters.status,
    ].filter(Boolean).length

    const startIndex = (paginationDetails.currentPage - 1) * paginationDetails.limit

    return (
        <>
            <section className="main-sec">
                <div className="row">
                    <div className="col-lg-6">
                        <div className="dashboard-title">
                            <h4 className="dash-head">
                                <i className="fa fa-chart-bar me-2" />
                                Purchase History
                            </h4>
                        </div>
                        <div className="custom-bredcump">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Purchase History
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>

                    <div className="col-lg-12">
                        <div className="cards bus-list">
                            <div className="bus-filter">
                                <div className="row align-items-center">
                                    <div className="col-lg-6">
                                        <h5 className="card-title">
                                            Purchase History ({paginationDetails.totalRecords || 0})
                                        </h5>
                                    </div>
                                </div>

                                <div className="purchase-toolbar mt-3">
                                    <div className="purchase-search">
                                        <i className="fa fa-search purchase-search-icon" />
                                        <input
                                            type="text"
                                            className="purchase-search-input"
                                            placeholder="Search by student name or email"
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                        />
                                    </div>

                                    <div className="purchase-filter-actions">
                                        <button
                                            className={`btn btn-outline-primary ${showFilters ? "active" : ""}`}
                                            onClick={() => setShowFilters((prev) => !prev)}
                                            type="button"
                                        >
                                            <i className="fa fa-filter me-1"></i>
                                            Filters
                                            {activeFilterCount > 0 && (
                                                <span className="badge bg-danger ms-1">{activeFilterCount}</span>
                                            )}
                                        </button>

                                        {hasActiveFilters() && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={clearFilters}
                                                type="button"
                                            >
                                                <i className="fa fa-times me-1"></i>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {showFilters && (
                                    <div className="filter-panel mt-3 p-3 bg-light rounded">
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label className="form-label">Payment Status</label>
                                                <select
                                                    className="form-control"
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange("status", e.target.value)}
                                                >
                                                    <option value="">All Status</option>
                                                    <option value="success">Success</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="failed">Failed</option>
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Start Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={filters.startDate}
                                                    max={filters.endDate || undefined}
                                                    onChange={(e) => handleFilterChange("startDate", e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">End Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={filters.endDate}
                                                    min={filters.startDate || undefined}
                                                    onChange={(e) => handleFilterChange("endDate", e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="table table-responsive custom-table">
                                <table className="table table-borderless">
                                    <thead>
                                        <tr>
                                            <th>Sr. no.</th>
                                            <th>Package Name</th>
                                            <th>Student Name</th>
                                            <th>Amount</th>
                                            <th>Payment Id</th>
                                            <th>Payment Status</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history?.length > 0 ? (
                                            history.map((arr, i) => (
                                                <tr key={arr?._id || i}>
                                                    <td>{startIndex + i + 1}</td>
                                                    <td>
                                                        <span className="fw-medium">
                                                            {arr?.packageId?.title || "N/A"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {arr?.userId?.name || "N/A"}
                                                        <p className="text-muted mb-0">{arr?.userId?.mobile || ""}</p>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold">{arr?.amount || "0"}</span>
                                                    </td>
                                                    <td>
                                                        <span>{arr?.paymentID || "N/A"}</span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={
                                                                arr?.status === "success"
                                                                    ? "btn btn-pill btn-success btn-sm"
                                                                    : arr?.status === "pending"
                                                                        ? "btn btn-pill btn-warning btn-sm"
                                                                        : "btn btn-pill btn-danger btn-sm"
                                                            }
                                                        >
                                                            {arr?.status === "success"
                                                                ? "Success"
                                                                : arr?.status === "pending"
                                                                    ? "Pending"
                                                                    : "Failed"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span>
                                                            {arr?.createdAt
                                                                ? moment(arr.createdAt).format("DD/MM/YYYY")
                                                                : "N/A"}
                                                        </span>
                                                        <br />
                                                        <small className="text-muted">
                                                            {arr?.createdAt ? moment(arr.createdAt).format("hh:mm A") : ""}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <div className="no-data">
                                                        <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                                                        <h5 className="text-muted">
                                                            {hasActiveFilters() ? "No Records Found" : "No Purchase History Found"}
                                                        </h5>
                                                        <p className="text-muted">
                                                            {hasActiveFilters()
                                                                ? "Try changing the search or filters to see more results."
                                                                : "There are no purchase records to display."}
                                                        </p>
                                                        {hasActiveFilters() && (
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={clearFilters}
                                                                type="button"
                                                            >
                                                                <i className="fa fa-times me-1"></i>
                                                                Clear Filters
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {paginationDetails.totalRecords > 0 && paginationDetails.totalPages > 1 && (
                                <Pagination
                                    pageNumber={paginationDetails.currentPage}
                                    totalPages={paginationDetails.totalPages}
                                    setPageNumber={setPageNumber}
                                    totalCount={paginationDetails.totalRecords}
                                    limit={paginationDetails.limit}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
                .purchase-toolbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .purchase-search {
                    position: relative;
                    flex: 1;
                    min-width: 260px;
                }

                .purchase-search-icon {
                    position: absolute;
                    top: 50%;
                    left: 14px;
                    transform: translateY(-50%);
                    color: #6c757d;
                }

                .purchase-search-input {
                    width: 100%;
                    padding: 12px 14px 12px 40px;
                    border: 1px solid #d8dee6;
                    border-radius: 10px;
                    outline: none;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }

                .purchase-search-input:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
                }

                .purchase-filter-actions {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .filter-panel {
                    border: 1px solid #e0e0e0;
                    animation: slideDown 0.3s ease-out;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }

                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .btn.active {
                    background-color: #007bff;
                    color: white;
                    border-color: #007bff;
                }

                @media (max-width: 767px) {
                    .purchase-search {
                        min-width: 100%;
                    }

                    .purchase-filter-actions {
                        width: 100%;
                        justify-content: flex-start;
                    }
                }
            `}</style>
        </>
    )
}
